import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, Grid, Card, CardContent, Tooltip,
  Collapse, IconButton,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import { ColumnDef } from '../../utils/exportUtils';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useReplenishmentFilters } from './ReplenishmentFilterContext';

const STATUS_OPTIONS = ['', 'pending', 'approved', 'transmitted', 'delivered', 'cancelled'];

const statusColor: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'info',
  transmitted: 'info',
  delivered: 'success',
  cancelled: 'error',
};

function defaultDateFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().slice(0, 10);
}

interface OrderLine {
  order_id: string;
  po_number: string | null;
  store_id: string;
  store_name: string;
  product_id: string;
  product_name: string;
  sku: string;
  brand: string | null;
  category: string | null;
  suggested_quantity: number;
  approved_quantity: number | null;
  final_quantity: number | null;
  quantity_confirmed: number | null;
  quantity_received: number | null;
  unit_cost: number;
  total_value: number;
  order_date: string | null;
  expected_delivery: string | null;
  actual_delivery: string | null;
  status: string;
  has_claim: boolean;
  claim_reason: string | null;
}

interface POGroup {
  groupKey: string;
  po_number: string;
  order_date: string | null;
  expected_delivery: string | null;
  actual_delivery: string | null;
  status: string;
  total_qty: number;
  total_approved: number;
  total_confirmed: number;
  total_received: number;
  total_value: number;
  has_claim: boolean;
  claim_reasons: string[];
  lines: OrderLine[];
  stores: string[];
}

const OrderHistoryTab: React.FC = () => {
  const { fmtCurrency, fmtCurrencyLabel } = useCurrencyFormat();
  const { brands, categories, stores, cities } = useReplenishmentFilters();
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedPOs, setExpandedPOs] = useState<Set<string>>(new Set());

  const { data: orders = [], isLoading, error } = useQuery({
    queryKey: ['orderHistory', dateFrom, dateTo, statusFilter, brands, categories, stores, cities],
    queryFn: async () => {
      const params: any = { limit: 500 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter) params.status = statusFilter;
      if (brands.length > 0) params.brands = brands.join(',');
      if (categories.length > 0) params.categories = categories.join(',');
      if (stores.length > 0) params.stores = stores.join(',');
      if (cities.length > 0) params.cities = cities.join(',');
      const res = await api.getOrderHistory(params);
      return res.data as OrderLine[];
    },
  });

  // Group orders by PO number
  const poGroups = useMemo(() => {
    const map = new Map<string, POGroup>();
    let noPOCounter = 0;

    for (const o of orders) {
      const key = o.po_number || `_no_po_${++noPOCounter}`;

      if (!map.has(key)) {
        map.set(key, {
          groupKey: key,
          po_number: o.po_number || `—`,
          order_date: o.order_date,
          expected_delivery: o.expected_delivery,
          actual_delivery: o.actual_delivery,
          status: o.status,
          total_qty: 0,
          total_approved: 0,
          total_confirmed: 0,
          total_received: 0,
          total_value: 0,
          has_claim: false,
          claim_reasons: [],
          lines: [],
          stores: [],
        });
      }

      const group = map.get(key)!;
      group.lines.push(o);
      group.total_qty += o.suggested_quantity || 0;
      group.total_approved += o.approved_quantity || 0;
      group.total_confirmed += o.quantity_confirmed || 0;
      group.total_received += o.quantity_received || 0;
      group.total_value += o.total_value || 0;
      if (o.has_claim) {
        group.has_claim = true;
        if (o.claim_reason) group.claim_reasons.push(o.claim_reason);
      }
      if (o.store_name && !group.stores.includes(o.store_name)) {
        group.stores.push(o.store_name);
      }
    }

    return Array.from(map.values());
  }, [orders]);

  // KPI calculations
  const kpis = useMemo(() => {
    const totalOrders = poGroups.length;
    const totalValue = poGroups.reduce((s, g) => s + g.total_value, 0);
    const delivered = poGroups.filter((g) => g.status === 'delivered').length;
    const pending = poGroups.filter((g) => ['pending', 'approved', 'transmitted'].includes(g.status)).length;
    const claims = poGroups.filter((g) => g.has_claim).length;
    return { totalOrders, totalValue, delivered, pending, claims };
  }, [poGroups]);

  const toggleExpand = useCallback((po: string) => {
    setExpandedPOs((prev) => {
      const next = new Set(prev);
      if (next.has(po)) next.delete(po);
      else next.add(po);
      return next;
    });
  }, []);

  const exportColumns: ColumnDef[] = [
    { key: 'po_number', header: 'PO Number' },
    { key: 'order_date', header: 'Order Date' },
    { key: 'expected_delivery', header: 'Expected Delivery' },
    { key: 'actual_delivery', header: 'Actual Delivery' },
    { key: 'store_name', header: 'Store' },
    { key: 'product_name', header: 'Product' },
    { key: 'sku', header: 'SKU' },
    { key: 'suggested_quantity', header: 'Qty Ordered' },
    { key: 'approved_quantity', header: 'Qty Approved' },
    { key: 'quantity_confirmed', header: 'Qty Confirmed' },
    { key: 'quantity_received', header: 'Qty Received' },
    { key: 'total_value', header: `Value ${fmtCurrencyLabel()}`, format: (v: number) => fmtCurrency(v) },
    { key: 'status', header: 'Order Status' },
    { key: 'claim_reason', header: 'Claim' },
  ];

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Orders', value: kpis.totalOrders, color: '#1976d2' },
          { label: 'Total Value', value: fmtCurrency(kpis.totalValue), color: '#388e3c' },
          { label: 'Delivered', value: kpis.delivered, color: '#2e7d32' },
          { label: 'Pending', value: kpis.pending, color: '#ed6c02' },
          { label: 'Claims', value: kpis.claims, color: kpis.claims > 0 ? '#d32f2f' : '#757575' },
        ].map((kpi) => (
          <Grid item xs={12} sm={6} md key={kpi.label}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h5" sx={{ color: kpi.color, fontWeight: 600 }}>
                  {kpi.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="From" type="date" size="small" value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To" type="date" size="small" value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Order Status</InputLabel>
          <Select value={statusFilter} label="Order Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.filter(Boolean).map((s) => (
              <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <ExportMenu
            data={orders}
            columns={exportColumns}
            filename="order-history"
            sheetName="Order History"
            title="Order History Report"
          />
        </Box>
      </Box>

      {/* Table */}
      {isLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load order history</Alert>}

      {!isLoading && !error && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>PO Number</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Expected Delivery</TableCell>
                <TableCell>Actual Delivery</TableCell>
                <TableCell>Store(s)</TableCell>
                <TableCell align="right">SKUs</TableCell>
                <TableCell align="right">Qty Ordered</TableCell>
                <TableCell align="right">Qty Approved</TableCell>
                <TableCell align="right">Qty Confirmed</TableCell>
                <TableCell align="right">Qty Received</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell>Order Status</TableCell>
                <TableCell>Claim</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {poGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                poGroups.map((group) => {
                  const isExpanded = expandedPOs.has(group.groupKey);
                  return (
                    <React.Fragment key={group.groupKey}>
                      {/* PO summary row */}
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isExpanded ? 'var(--color-accent-bg, #f5f0e8)' : 'inherit',
                          '& > td': { fontWeight: 600, borderBottom: isExpanded ? 'none' : undefined },
                        }}
                        onClick={() => toggleExpand(group.groupKey)}
                      >
                        <TableCell sx={{ width: 40, px: 0.5 }}>
                          <IconButton size="small" sx={{ width: 24, height: 24 }}>
                            {isExpanded
                              ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                              : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-accent-hover, #722F37)' }}>
                          {group.po_number}
                        </TableCell>
                        <TableCell>{formatDate(group.order_date)}</TableCell>
                        <TableCell>{formatDate(group.expected_delivery)}</TableCell>
                        <TableCell>{formatDate(group.actual_delivery)}</TableCell>
                        <TableCell>
                          {group.stores.length <= 2
                            ? group.stores.join(', ')
                            : `${group.stores[0]} +${group.stores.length - 1}`}
                        </TableCell>
                        <TableCell align="right">{group.lines.length}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{group.total_qty.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{group.total_approved.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{group.total_confirmed ? group.total_confirmed.toLocaleString() : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{group.total_received ? group.total_received.toLocaleString() : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(group.total_value)}</TableCell>
                        <TableCell>
                          <Chip
                            label={group.status}
                            size="small"
                            color={statusColor[group.status] || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {group.has_claim && (
                            <Tooltip title={group.claim_reasons.join('; ') || 'Claim'} arrow>
                              <WarningAmberIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded SKU detail rows */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={14} sx={{ p: 0, bgcolor: 'var(--color-bg-secondary, #fafaf7)' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 3, py: 1.5 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>SKU</TableCell>
                                      <TableCell sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Item Status</TableCell>
                                      <TableCell sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Product</TableCell>
                                      <TableCell sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Store</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qty Ordered</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qty Approved</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qty Confirmed</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Qty Received</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Unit Price</TableCell>
                                      <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Value</TableCell>
                                      <TableCell sx={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)' }}>Claim</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {group.lines.map((line) => {
                                      const matchesFilter =
                                        (brands.length > 0 && line.brand && brands.includes(line.brand)) ||
                                        (categories.length > 0 && line.category && categories.includes(line.category));
                                      return (
                                      <TableRow
                                        key={line.order_id}
                                        sx={{
                                          '&:last-child td': { borderBottom: 0 },
                                          ...(matchesFilter ? {
                                            bgcolor: 'rgba(211, 47, 47, 0.07)',
                                            borderLeft: '3px solid #d32f2f',
                                          } : {}),
                                        }}
                                      >
                                        <TableCell sx={{ fontSize: '12px', fontFamily: 'monospace' }}>{line.sku}</TableCell>
                                        <TableCell sx={{ py: '6px', px: '8px' }}>
                                          {(line as any).lifecycle_stage ? (
                                            <Chip
                                              label={(line as any).lifecycle_stage}
                                              size="small"
                                              color={
                                                (line as any).lifecycle_stage === 'NEW' ? 'info' :
                                                (line as any).lifecycle_stage === 'GROWTH' ? 'success' :
                                                (line as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                                                ((line as any).lifecycle_stage === 'PHASE_OUT' || (line as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                                                'default'
                                              }
                                              variant="outlined"
                                              sx={{ fontSize: '10px', height: 20 }}
                                            />
                                          ) : (
                                            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '12px' }}>{line.product_name}</TableCell>
                                        <TableCell sx={{ fontSize: '12px' }}>{line.store_name}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{line.suggested_quantity}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{line.approved_quantity ?? '—'}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{line.quantity_confirmed ?? '—'}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{line.quantity_received ?? '—'}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(line.unit_cost)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(line.total_value)}</TableCell>
                                        <TableCell>
                                          {line.has_claim && (
                                            <Tooltip title={line.claim_reason || 'Claim'} arrow>
                                              <WarningAmberIcon sx={{ fontSize: 16 }} color="warning" />
                                            </Tooltip>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OrderHistoryTab;
